import { format } from 'date-fns';

/**
 * Parses a legacy REST date or datetime (in UTC) into a local `Date` object.
 *
 * This function interprets the input as a UTC timestamp and creates a
 * `Date` instance whose time is set to midnight (00:00:00.000) in the
 * **local timezone**.
 *
 * For example:
 * - Input: `"2025-11-13T00:00:00.000Z"`
 * - Output: `Date(2025-11-13T00:00:00.000 [local timezone])`
 *
 * This is used to convert legacy REST responses that encode **dates**
 * as UTC datetimes set to midnight.
 *
 * @param utc - A UTC date/datetime string in ISO 8601 / RFC 3339 format.
 * @returns A `Date` instance representing the same calendar day in local time.
 */
export function parseLegacyDate(utc: string): Date {
  const d = new Date(utc);
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

/**
 * Parses a legacy UTC datetime (RFC 3339) into a native Date instance.
 *
 * Supported examples:
 *   - "2025-11-13T14:35:27.123Z"
 *   - "2025-11-13T14:35:27Z"
 *   - "2025-11-13T14:35:27.123+02:00"
 *
 * The resulting Date is in the browser's local timezone, representing
 * the correct instant in time.
 */
export function parseLegacyDateTime(iso: string): Date {
  return new Date(iso);
}

/**
 * Serializes a local `Date` object into a UTC datetime string suitable for
 * legacy REST services that expect date-only fields encoded as UTC datetimes
 * with the time set to midnight (00:00:00.000Z).
 *
 * Only the date components (year, month, day) are preserved — the time part
 * is always normalized to midnight UTC.
 *
 * For example:
 * - Input:  `Date(2025-11-13T14:30:00.000 [local timezone])`
 * - Output: `"2025-11-13T00:00:00.000Z"`
 *
 * @param local - A `Date` instance in local time.
 * @returns An ISO 8601 string (UTC) representing the same calendar date at midnight.
 */
export function formatLegacyDate(local: Date): string {
  return new Date(Date.UTC(local.getFullYear(), local.getMonth(), local.getDate())).toISOString();
}

export function formatLegacyDateTime(local: Date): string {
  return local.toISOString();
}

export function formatLegacyDateOnlyTime(local: Date): string {
  return format(local, 'yyyy-MM-dd');
}

const ISO_DATE_OR_DATETIME_REGEX =
  /^(?:\d{4}-\d{2}-\d{2}$|\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?(?:Z|[+-]\d{2}:\d{2}))$/;

/**
 * Checks whether a string strictly matches one of the supported ISO date formats:
 * - "yyyy-MM-dd" (date only)
 * - "yyyy-MM-ddTHH:mm:ss.SSSZ" (UTC datetime)
 * - "yyyy-MM-ddTHH:mm:ss.SSS±HH:mm" (datetime with offset)
 */
export function isIsoDateOrDateTime(value: unknown): boolean {
  return typeof value === 'string' && ISO_DATE_OR_DATETIME_REGEX.test(value);
}
